// Rendering vocabulary shared by the reconciliation pane and the removal card (extracted so the
// two tables never drift, the TablePaginator argument of Phase 5, W-12).
import { formatDateTime } from '../../../utils/billingUtils'

const KEY_DIAGNOSTIC = "key '"

// Unknown backend vocabulary is shown humanised (`awaiting_merchant_signature` → "Awaiting
// merchant signature") rather than as a bare key path or a raw enum token.
export const humanise = value => {
  const text = String(value).replace(/_/g, ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// Backend vocabulary renders through i18n when known and humanised when not: a value the
// platform adds later must never surface as a bare `cycle_counts.…` key path.
// (`exists` is not enough: a value such as `constructor` resolves to an inherited function, and
// i18next answers an object-valued lookup such as `__proto__` with an English diagnostic sentence.)
export const makeVocab = (i18n, T) => (group, value) => {
  if (value == null) return ''
  const key = `${T}.${group}.${value}`
  const translated = i18n.t(key)
  return typeof translated === 'string' && translated !== key && !translated.startsWith(KEY_DIAGNOSTIC) ? translated : humanise(value)
}

export const OUTCOME_COLORS = {
  pending: 'bg-gray-100 text-gray-800',
  executed: 'bg-green-100 text-green-800',
  partial: 'bg-amber-100 text-amber-800',
  // A skip is benign; a block leaves units behind in Sobrantes — the two must not share a colour.
  skipped: 'bg-white border border-gray-300 text-gray-600',
  blocked: 'bg-red-100 text-red-800',
  // An outcome this build does not know: neutral, and visibly not "pending".
  unknown: 'bg-white border border-dashed border-gray-300 text-gray-600',
}
export const outcomeClass = outcome => (Object.prototype.hasOwnProperty.call(OUTCOME_COLORS, outcome) ? OUTCOME_COLORS[outcome] : OUTCOME_COLORS.unknown)

export const cell = 'px-3 py-1.5 text-sm'
// The header's timestamps arrive as ISO-8601(6) strings; banners and cells read them out loud,
// so render them in the operator's language through the shared WMS formatter, never raw. A value
// that is not a date reads as absent, never "Invalid Date": every caller falls back (the
// placeholder, a dash, a sentence without the date) or omits the line.
export const formatTimestamp = (value, language) => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : formatDateTime(value, language)
}
// An absent value, lighter than a real one and never readable as a minus sign.
export const placeholder = <span className="text-gray-400">—</span>
// Between two items of the reason cell: trails the previous item so a wrap never starts a line
// with it, and is decoration only for assistive tech.
export const separator = (
  <span aria-hidden="true" className="mx-1 text-gray-500">
    ·
  </span>
)
